import { H6 } from "@sellar-io/components";
import { ContainerTypesEnumType } from "@sellar-io/api-v2/types";
import { getDisplayNameByKey, orderableItemValues } from "@sellar-io/common";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon } from "../../../../components";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";

import { DiscoveryFilterButton } from "../components/DiscoveryFilterButton";
import { FilterDrawerHeader } from "../components/DiscoveryMobileFilterDrawer";
import {
  genericContainerTypes,
  specificContainerTypes,
  useDiscoveryV2SearchStore,
} from "../DiscoveryV2SearchStore";
import { CollapsableFilterCard } from "./FilterCard";

export const FilterContainerCard = ({ onClick }: { onClick?: () => void }) => {
  const isDesktop = useMediaQuery("(min-width: 1084px)");
  const [showAllContainerTypes, setShowAllContainerTypes] = useState(false);

  const {
    selectedContainerTypes,
    removeContainerType,
    addContainerType,
    setContainerTypes,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      selectedContainerTypes: state.selectedContainerTypes,
      addContainerType: state.addContainerType,
      removeContainerType: state.removeContainerType,
      setContainerTypes: state.setContainerTypes,
    })),
  );

  // Check cask first so "Cask - Kegstar" is not classified as a keg.
  const type = selectedContainerTypes.includes("cask")
    ? "cask"
    : selectedContainerTypes.some((containerType) =>
          containerType.toLowerCase().includes("keg"),
        )
      ? "keg"
      : "default";
  const resolvedType = isDesktop ? type : "default";
  const isDefaultType = resolvedType === "default";
  const shownSpecificContainerTypes =
    resolvedType === "default"
      ? undefined
      : specificContainerTypes[resolvedType];
  const shouldShowAllContainerTypes =
    showAllContainerTypes || selectedContainerTypes.includes("bagInBox");

  const getShownContainerTypes = () => {
    if (shownSpecificContainerTypes) return shownSpecificContainerTypes;
    if (shouldShowAllContainerTypes) return genericContainerTypes;
    return genericContainerTypes.filter((type) =>
      (
        ["keg", "cask", "can", "bottle"] satisfies Partial<
          typeof genericContainerTypes
        >
      ).includes(type),
    );
  };

  return (
    <>
      {isDesktop ? (
        <CollapsableFilterCard
          header={
            <div className={"flex items-center"}>
              <H6
                className={twMerge(!isDefaultType && "cursor-pointer")}
                onClick={() => {
                  if (!isDefaultType) {
                    setShowAllContainerTypes(false);
                    setContainerTypes([]);
                  }
                }}
              >
                Container
              </H6>
              {!isDefaultType ? (
                <>
                  <Icon icon={"CHEVRON_RIGHT"} />
                  {resolvedType === "keg"
                    ? "Keg"
                    : getDisplayNameByKey(orderableItemValues, resolvedType)}
                </>
              ) : null}
            </div>
          }
        >
          {getShownContainerTypes().map((container) => (
            <DiscoveryFilterButton
              selected={selectedContainerTypes.includes(container)}
              key={container}
              title={
                container === "keg"
                  ? "Keg"
                  : (getDisplayNameByKey(orderableItemValues, container) ?? "")
              }
              onClick={() => {
                if (selectedContainerTypes.includes(container)) {
                  return removeContainerType(container);
                }
                if (!isDefaultType) {
                  setContainerTypes([
                    ...selectedContainerTypes.filter(
                      (containerType) =>
                        resolvedType !== "keg" ||
                        containerType !== resolvedType,
                    ),
                    container,
                  ]);
                }
                addContainerType(container);
              }}
            />
          ))}
          {isDefaultType && !shouldShowAllContainerTypes && (
            <DiscoveryFilterButton
              title={"More"}
              onClick={() => setShowAllContainerTypes(true)}
            />
          )}
        </CollapsableFilterCard>
      ) : (
        <div className={"flex flex-col"}>
          <FilterDrawerHeader title={"Container"} onClick={onClick} />
          <div className={"p-4"}>
            <InputContainer>
              <InputSelect
                onChange={(e) => {
                  if (e.target.value === "all") {
                    return setContainerTypes([]);
                  }
                  setContainerTypes([e.target.value as ContainerTypesEnumType]);
                  addContainerType(e.target.value as ContainerTypesEnumType);
                }}
              >
                <InputOption selected={!selectedContainerTypes} value={"all"}>
                  All
                </InputOption>
                {genericContainerTypes.map((containerType) => (
                  <InputOption
                    selected={selectedContainerTypes.includes(containerType)}
                    key={containerType}
                    value={containerType}
                  >
                    {containerType === "keg"
                      ? "Keg"
                      : getDisplayNameByKey(orderableItemValues, containerType)}
                  </InputOption>
                ))}
              </InputSelect>
            </InputContainer>
            {shownSpecificContainerTypes ? (
              <div className={"mt-4 flex flex-col gap-1"}>
                {shownSpecificContainerTypes.map((container) => (
                  <DiscoveryFilterButton
                    key={container}
                    selected={selectedContainerTypes.includes(container)}
                    title={
                      getDisplayNameByKey(orderableItemValues, container) ??
                      container
                    }
                    onClick={() => {
                      if (selectedContainerTypes.includes(container)) {
                        return removeContainerType(container);
                      }
                      if (!isDefaultType) {
                        setContainerTypes([
                          ...selectedContainerTypes.filter(
                            (containerType) =>
                              resolvedType !== "keg" ||
                              containerType !== resolvedType,
                          ),
                          container,
                        ]);
                      }
                      addContainerType(container);
                    }}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
};
